// Super-admin "Open their dashboard": the Invoice Chaser console (ai.mploi.com)
// mints a one-time token and sends the admin's browser to /ar?super_user_token=...
// on core.mploi.com or the customer's whitelabel domain. This module swaps that
// token for the customer's real session before any route guard can bounce the
// visitor to the sign-in page.
import { cookieService, userService } from "../_services";
import { STRING_CONSTANTS } from "./constants/stringConstants";

export const SUPER_USER_TOKEN_PARAM = "super_user_token";

export function getSuperUserTokenFromUrl(): string | null {
  try {
    return new URLSearchParams(window.location.search).get(SUPER_USER_TOKEN_PARAM);
  } catch (e) {
    return null;
  }
}

/** The current page with the token removed (any other query params are kept). */
export function urlWithoutSuperUserToken(): string {
  const url = new URL(window.location.href);
  url.searchParams.delete(SUPER_USER_TOKEN_PARAM);
  return url.pathname + url.search + url.hash;
}

/**
 * Call this before React renders. A super-user link always means "become this
 * customer", so whatever session is in the browser (an earlier login-as, or the
 * admin's own) is dropped first. It also stops the API client from attaching a
 * stale Authorization header to the exchange call, which the backend would
 * reject and the client would turn into a redirect to /signin.
 */
export function prepareSuperUserLogin(): void {
  if (getSuperUserTokenFromUrl()) {
    cookieService.removeAllCookies();
  }
}

/**
 * Exchange the one-time token for the customer's session and store it exactly
 * the way a normal sign-in does (see SignIn.tsx): the login payload becomes the
 * user cookie and the JWT access string becomes the token cookie.
 */
export async function exchangeSuperUserToken(token: string): Promise<{ ok: boolean; message?: string }> {
  const response: any = await userService.aiRequest(
    { method: "validate-super-user-token", data: { super_user_token: token } },
    true
  );
  const body = response?.data;
  if (body?.success && body?.user && body?.token) {
    cookieService.removeAllCookies();
    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.user, body.user);
    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.token, body.token);
    cookieService.saveCookie(STRING_CONSTANTS.cookieStrings.isGuest, false);
    return { ok: true };
  }
  return {
    ok: false,
    message: body?.message || body?.error || "The admin session link is invalid or has expired.",
  };
}
